/**
 * SmartCMR Error Diagnostics Collector
 * Collects comprehensive system/browser/user info for error reports
 */

// ═══ Action Tracker ═══
const MAX_ACTIONS = 15;
let _actionLog = [];

export const trackAction = (action) => {
  _actionLog.push({
    action,
    time: new Date().toISOString(),
    url: window.location.pathname,
  });
  if (_actionLog.length > MAX_ACTIONS) _actionLog.shift();
};

export const getRecentActions = () => [..._actionLog];

// ═══ Browser & OS Detection ═══
const parseBrowser = (ua) => {
  if (!ua) return 'Onbekend';
  if (ua.includes('CriOS')) return 'Chrome iOS ' + (ua.match(/CriOS\/(\d+)/)?.[1] || '');
  if (ua.includes('FxiOS')) return 'Firefox iOS';
  if (ua.includes('EdgA') || ua.includes('Edg/')) return 'Edge ' + (ua.match(/Edg[eA]?\/(\d+)/)?.[1] || '');
  if (ua.includes('OPR') || ua.includes('Opera')) return 'Opera';
  if (ua.includes('SamsungBrowser')) return 'Samsung Browser';
  if (ua.includes('Chrome') && !ua.includes('Edg')) return 'Chrome ' + (ua.match(/Chrome\/(\d+)/)?.[1] || '');
  if (ua.includes('Safari') && !ua.includes('Chrome')) return 'Safari ' + (ua.match(/Version\/(\d+\.\d+)/)?.[1] || '');
  if (ua.includes('Firefox')) return 'Firefox ' + (ua.match(/Firefox\/(\d+)/)?.[1] || '');
  return ua.substring(0, 60);
};

const parseOS = (ua) => {
  if (!ua) return 'Onbekend';
  if (ua.includes('iPhone')) return 'iOS (iPhone)';
  if (ua.includes('iPad')) return 'iOS (iPad)';
  if (ua.includes('Android')) return 'Android ' + (ua.match(/Android (\d+[\.\d]*)/)?.[1] || '');
  if (ua.includes('Windows NT 10')) return 'Windows 10/11';
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS X')) return 'macOS ' + (ua.match(/Mac OS X (\d+[_\.\d]*)/)?.[1]?.replace(/_/g, '.') || '');
  if (ua.includes('Linux')) return 'Linux';
  return 'Onbekend';
};

// ═══ Environment Detection ═══
const getEnvironment = () => {
  const host = window.location.hostname;
  if (host.includes('preview.emergentagent') || host === 'localhost') return 'Preview (Dev)';
  if (host.includes('smartcmr.nl')) return 'Productie';
  return `Overig (${host})`;
};

// ═══ System Stats ═══
const getSystemStats = () => {
  const stats = {};
  try {
    stats.online = navigator.onLine;
    stats.screen = `${window.screen.width}x${window.screen.height}`;
    stats.viewport = `${window.innerWidth}x${window.innerHeight}`;
    stats.devicePixelRatio = window.devicePixelRatio || 1;
    stats.language = navigator.language;
    stats.cookiesEnabled = navigator.cookieEnabled;
    if (navigator.connection) {
      stats.connectionType = navigator.connection.effectiveType || 'unknown';
      stats.downlink = navigator.connection.downlink;
    }
    if (performance?.memory) {
      const mem = performance.memory;
      stats.jsHeapUsed = Math.round(mem.usedJSHeapSize / 1048576) + ' MB';
      stats.jsHeapTotal = Math.round(mem.totalJSHeapSize / 1048576) + ' MB';
      stats.jsHeapLimit = Math.round(mem.jsHeapSizeLimit / 1048576) + ' MB';
    }
    if (performance?.now) {
      stats.pageUptime = Math.round(performance.now() / 1000) + 's';
    }
    const navEntry = performance?.getEntriesByType?.('navigation')?.[0];
    if (navEntry) {
      stats.pageLoadTime = Math.round(navEntry.loadEventEnd - navEntry.startTime) + 'ms';
    }
  } catch { /* silent */ }
  return stats;
};

// ═══ User Info from localStorage/token ═══
const getUserInfo = () => {
  try {
    const token = localStorage.getItem('smartcmr_token') || localStorage.getItem('token');
    if (!token) return { authenticated: false, session_id: getSessionId() };
    const payload = JSON.parse(atob(token.split('.')[1]));
    return {
      authenticated: true,
      user_id: payload.user_id || payload.sub || 'unknown',
      email: payload.email || 'unknown',
      role: payload.role || 'unknown',
      company_name: payload.company_name || '',
      company_id: payload.company_id || '',
    };
  } catch {
    return { authenticated: false, session_id: getSessionId() };
  }
};

const getSessionId = () => {
  let sid = sessionStorage.getItem('smartcmr_session_id');
  if (!sid) {
    sid = 'SES-' + Math.random().toString(36).substring(2, 10).toUpperCase();
    sessionStorage.setItem('smartcmr_session_id', sid);
  }
  return sid;
};

// ═══ Page/Component from URL ═══
const getPageInfo = (url) => {
  try {
    const path = new URL(url).pathname;
    const segments = path.split('/').filter(Boolean);
    const routeMap = {
      'dashboard': 'Dashboard',
      'cmr': segments[1] === 'new' ? 'Nieuwe CMR' : segments[1] === 'history' ? 'CMR Historie' : 'CMR Bekijken',
      'fustbon': segments[1] === 'history' ? 'Fustbon Historie' : 'Nieuwe Fustbon',
      'subscription': segments[1] === 'success' ? 'Betaling Succesvol' : 'Abonnement',
      'admin-panel': 'Admin Panel' + (segments[1] ? ' → ' + segments[1].charAt(0).toUpperCase() + segments[1].slice(1) : ''),
      'admin': 'Admin',
      'drivers': 'Chauffeurs',
      'customers': 'Klanten',
      'company-settings': 'Bedrijfsinstellingen',
      'login': 'Login',
      'register': 'Registratie',
      'demo': 'Demo',
      'contact': 'Contact',
      'blogs': 'Blogs',
      'incidents': 'Incidenten',
      'locations': 'Vaste Locaties',
    };
    return {
      page: routeMap[segments[0]] || path,
      route: path,
    };
  } catch {
    return { page: 'Onbekend', route: url };
  }
};

// ═══ Console Error Capture ═══
let _consoleErrors = [];
const MAX_CONSOLE = 10;

const _origError = console.error;
console.error = function(...args) {
  _consoleErrors.push({
    message: args.map(a => typeof a === 'object' ? JSON.stringify(a)?.substring(0, 200) : String(a)?.substring(0, 200)).join(' '),
    time: new Date().toISOString(),
  });
  if (_consoleErrors.length > MAX_CONSOLE) _consoleErrors.shift();
  _origError.apply(console, args);
};

export const getConsoleErrors = () => [..._consoleErrors];

// ═══ Main Collector ═══
export const collectDiagnostics = (error, errorInfo, errorCode) => {
  const ua = navigator.userAgent;
  const pageInfo = getPageInfo(window.location.href);

  return {
    // Summary
    error_code: errorCode,
    timestamp: new Date().toISOString(),
    environment: getEnvironment(),
    browser: parseBrowser(ua),
    os: parseOS(ua),
    user_agent: ua,

    // Page
    url: window.location.href,
    page: pageInfo.page,
    route: pageInfo.route,

    // User
    user: getUserInfo(),

    // Error details
    message: error?.message || 'Unknown error',
    error_name: error?.name || 'Error',
    stack: error?.stack?.substring(0, 3000) || '',
    component_stack: errorInfo?.componentStack?.substring(0, 2000) || '',

    // Context
    recent_actions: getRecentActions(),
    console_errors: getConsoleErrors(),

    // System
    system: getSystemStats(),
  };
};
